import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Spinner } from "@/components/ui-bits";
import { ArrowLeft, Plus, Edit2, Trash2, Search, X, Home, Ship as ShipIcon, Share2, Key, Clock, Power } from "lucide-react";
import { toast } from "sonner";
import { fileToResizedDataUrl } from "@/lib/image";

export function AdminHeader({ title, onNew }) {
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full bg-white border border-[var(--border)] flex items-center justify-center"><ArrowLeft size={18} /></button>
      <h1 className="sns-title text-2xl flex-1">{title}</h1>
      {onNew && (
        <button onClick={onNew} className="sns-fab w-11 h-11 flex items-center justify-center" data-testid="btn-new-admin">
          <Plus size={20} strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function FiltersBar({ filter, setFilter, query, setQuery, withActive = true }) {
  return (
    <div className="sns-card p-3 mb-4 flex flex-wrap items-center gap-2">
      {withActive && (
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          {[["todos","Todos"],["true","Activos"],["false","Inactivos"]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter===k ? "bg-[var(--ink)] text-white" : "text-slate-600"}`}
                    data-testid={`filter-${k}`}>{l}</button>
          ))}
        </div>
      )}
      <div className="relative flex-1 min-w-[180px]">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" placeholder="Buscar..." value={query} onChange={(e) => setQuery(e.target.value)}
               className="sns-input pl-9 py-2 text-sm" data-testid="admin-search" />
        {query && <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><X size={14} /></button>}
      </div>
    </div>
  );
}

// ---------------- Clientes ----------------
export function GestionClientes() {
  const [items, setItems] = useState([]);
  const [embs, setEmbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [query, setQuery] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "todos") params.active = filter;
      if (query) params.q = query;
      const [cRes, eRes] = await Promise.all([
        api.get("/clientes", { params }),
        api.get("/embarcaciones"),
      ]);
      setItems(cRes.data);
      setEmbs(eRes.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter, query]);

  const propsByCliente = useMemo(() => {
    const map = {};
    embs.forEach((e) => {
      if (!map[e.cliente_id]) map[e.cliente_id] = [];
      map[e.cliente_id].push(e);
    });
    return map;
  }, [embs]);

  const del = async (id) => {
    const n = (propsByCliente[id] || []).length;
    if (n > 0) return alert(`Este cliente tiene ${n} propiedad(es) vinculadas. Elimínalas o reasígnalas primero.`);
    if (!confirm("¿Eliminar cliente?")) return;
    await api.delete(`/clientes/${id}`); reload();
  };

  return (
    <div className="px-5 pt-8 pb-32 max-w-5xl mx-auto" data-testid="page-clientes">
      <AdminHeader title="Clientes" onNew={() => setShowForm({ active: true, pais: "España" })} />
      <FiltersBar filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} />
      {loading ? <Spinner /> : (
        <div className="space-y-3">
          {items.map((c) => {
            const props = propsByCliente[c.id] || [];
            return (
              <div key={c.id} className="sns-card p-4 md:flex md:items-center md:gap-4" data-testid={`cliente-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold">{c.empresa || c.nombre || "—"}</div>
                    <span className="sns-pill bg-[var(--sky)] text-[var(--sky-text)] text-[10px]" data-testid={`props-count-${c.id}`}>
                      🛥 {props.length} propiedad{props.length !== 1 ? "es" : ""}
                    </span>
                    {!c.active && <span className="sns-pill bg-slate-200 text-slate-500 text-[10px]">INACTIVO</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate mt-1">
                    {c.cif && `CIF ${c.cif} · `}
                    {c.email && `${c.email} · `}
                    {c.tel1 && c.tel1}
                  </div>
                  {(c.direccion || c.poblacion) && (
                    <div className="text-xs text-slate-400 truncate mt-0.5">
                      {[c.direccion, c.cp, c.poblacion, c.provincia].filter(Boolean).join(", ")}
                    </div>
                  )}
                  {props.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap mt-2">
                      {props.slice(0, 4).map((e) => (
                        <span key={e.id} className="sns-pill bg-slate-100 text-slate-600 text-[10px]">
                          {e.tipo === "chalet" ? "🏠" : "🛥"} {e.name}
                        </span>
                      ))}
                      {props.length > 4 && <span className="text-[10px] text-slate-400 py-1.5">+{props.length - 4} más</span>}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-2 md:mt-0">
                  <button onClick={() => setShowForm(c)} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><Edit2 size={14} /></button>
                  <button onClick={() => del(c.id)} className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-600"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <p className="text-center text-slate-400 py-10">Sin clientes</p>}
        </div>
      )}
      {showForm !== null && <ClienteModal initial={showForm} propiedades={propsByCliente[showForm.id] || []} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); reload(); }} />}
    </div>
  );
}

function ClienteModal({ initial, propiedades = [], onClose, onSaved }) {
  const [d, setD] = useState(initial);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("data");
  const set = (k, v) => setD({ ...d, [k]: v });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const payload = { ...d };
      if (initial.id) await api.patch(`/clientes/${initial.id}`, payload);
      else await api.post("/clientes", payload);
      onSaved();
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };
  const tabs = [["data","Datos"], ["notas","Notas"]];
  if (initial.id) { tabs.push(["propiedades", `Propiedades (${propiedades.length})`]); tabs.push(["trabajos", "Trabajos"]); }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") {
            e.preventDefault();
          }
        }}
        className="bg-white rounded-3xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cliente-modal">
        <h3 className="sns-title text-xl mb-4">{initial.id ? "Editar cliente" : "Nuevo cliente"}</h3>

        <div className="flex gap-2 mb-4 border-b border-[var(--border)]">
          {tabs.map(([k,l]) => (
            <button type="button" key={k} onClick={() => setTab(k)}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab===k ? "border-[var(--coral)] text-[var(--ink)]" : "border-transparent text-slate-500"}`}
                    data-testid={`cli-tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "data" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Inp label="CIF" v={d.cif} s={(v) => set("cif", v)} />
              <Inp label="Empresa" v={d.empresa} s={(v) => set("empresa", v)} />
              <Inp label="Nombre" v={d.nombre} s={(v) => set("nombre", v)} required />
              <Inp label="Apellidos" v={d.apellidos} s={(v) => set("apellidos", v)} />
              <div className="col-span-2"><Inp label="Dirección" v={d.direccion} s={(v) => set("direccion", v)} /></div>
              <Inp label="CP" v={d.cp} s={(v) => set("cp", v)} />
              <Inp label="Población" v={d.poblacion} s={(v) => set("poblacion", v)} />
              <Inp label="Provincia" v={d.provincia} s={(v) => set("provincia", v)} />
              <Inp label="País" v={d.pais} s={(v) => set("pais", v)} />
              <Inp label="Teléfono 1" v={d.tel1} s={(v) => set("tel1", v)} />
              <Inp label="Teléfono 2" v={d.tel2} s={(v) => set("tel2", v)} />
              <Inp label="Email" type="email" v={d.email} s={(v) => set("email", v)} />
              <Inp label="Página web" v={d.url} s={(v) => set("url", v)} />
            </div>
            <label className="inline-flex items-center gap-2 mt-3">
              <input type="checkbox" checked={!!d.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-emerald-600" />
              <span className="text-sm">Cliente activo</span>
            </label>
          </>
        )}

        {tab === "notas" && (
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas internas sobre el cliente</label>
            <textarea value={d.notas || ""} onChange={(e) => set("notas", e.target.value)} rows={8} className="sns-input resize-none" />
          </div>
        )}

        {tab === "propiedades" && (
          <PropiedadesList propiedades={propiedades} clienteNombre={d.empresa || d.nombre} />
        )}

        {tab === "trabajos" && (
          <TrabajosCliente propiedades={propiedades} />
        )}

        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mt-3">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button disabled={saving} className="sns-btn sns-btn-accent flex-1" data-testid="cliente-save">{saving ? "..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

function PropiedadesList({ propiedades, clienteNombre }) {
  const nav = useNavigate();
  const barcos = propiedades.filter((p) => p.tipo === "embarcacion");
  const chalets = propiedades.filter((p) => p.tipo === "chalet");

  if (propiedades.length === 0) {
    return (
      <div className="text-center py-10 text-slate-400">
        <p className="mb-3">Este cliente aún no tiene propiedades vinculadas.</p>
        <p className="text-xs">Añádelas desde Gestión → Chalets / Embarcaciones, eligiendo <strong>{clienteNombre}</strong> como cliente.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {barcos.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShipIcon size={14} className="text-slate-500" />
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Embarcaciones ({barcos.length})</div>
          </div>
          <div className="space-y-2">
            {barcos.map((p) => <PropCard key={p.id} p={p} onClick={() => nav(`/gestion/embarcaciones?id=${p.id}`)} />)}
          </div>
        </div>
      )}
      {chalets.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Home size={14} className="text-slate-500" />
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Chalets ({chalets.length})</div>
          </div>
          <div className="space-y-2">
            {chalets.map((p) => <PropCard key={p.id} p={p} onClick={() => nav(`/gestion/embarcaciones?id=${p.id}`)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

const PropCard = ({ p, onClick }) => (
  <button type="button" onClick={onClick} className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors text-left" data-testid={`prop-${p.id}`}>
    {p.photo_url ? (
      <img src={p.photo_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
    ) : (
      <div className="w-12 h-12 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center shrink-0">
        {p.tipo === "chalet" ? <Home size={18} /> : <ShipIcon size={18} />}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="font-bold truncate">{p.name}</div>
      <div className="text-xs text-slate-500 truncate">{p.matricula || p.puerto || p.description || "—"}</div>
    </div>
    <span className="text-slate-300">›</span>
  </button>
);

function TrabajosCliente({ propiedades }) {
  const nav = useNavigate();
  const [mants, setMants] = useState([]);
  const [servs, setServs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [holdedTarget, setHoldedTarget] = useState(null); // mantenimiento id
  const embIds = propiedades.map((p) => p.id);

  const reload = async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([api.get("/mantenimientos"), api.get("/servicios")]);
      setMants(mRes.data.filter((x) => embIds.includes(x.embarcacion_id)));
      setServs(sRes.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [embIds.join(",")]);

  const servMap = Object.fromEntries(servs.map((s) => [s.id, s]));
  const embMap = Object.fromEntries(propiedades.map((p) => [p.id, p]));

  const del = async (mid) => {
    if (!confirm("¿Eliminar este mantenimiento?")) return;
    try { await api.delete(`/mantenimientos/${mid}`); reload(); }
    catch (ex) { alert(fmtErr(ex)); }
  };

  if (propiedades.length === 0) {
    return <div className="text-center py-10 text-slate-400 text-sm">Este cliente aún no tiene propiedades vinculadas.</div>;
  }

  return (
    <div>
      {msg && (
        <div className={`text-sm rounded-xl p-3 mb-3 ${msg.type === "ok" ? "bg-green-50 text-green-800 border border-green-200" : msg.type === "warn" ? "bg-yellow-50 text-yellow-900 border border-yellow-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="holded-msg">
          <div className="font-bold mb-1">{msg.title}</div>
          <div className="text-xs whitespace-pre-wrap">{msg.body}</div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">{mants.length} mantenimiento{mants.length !== 1 ? "s" : ""}</p>
        <button type="button" onClick={() => nav("/nuevo-mantenimiento")} className="sns-btn sns-btn-accent py-2 px-3 text-sm" data-testid="btn-new-mant-cliente">
          <Plus size={14} /> Nuevo
        </button>
      </div>
      {loading ? <Spinner /> : mants.length === 0 ? (
        <p className="text-center text-slate-400 py-6 text-sm">Sin mantenimientos todavía.</p>
      ) : (
        <div className="space-y-2">
          {mants.map((m) => {
            const emb = embMap[m.embarcacion_id];
            const st = m.status;
            const realizados = (m.services || []).filter((s) => s.realizado);
            const total = realizados.reduce((acc, s) => acc + (servMap[s.service_id]?.price || 0), 0);
            return (
              <div key={m.id} className="sns-card p-3 flex items-center gap-3" data-testid={`cli-trabajo-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="font-bold truncate">{emb?.name}</span>
                    <span className={`sns-pill text-[10px] st-${st}`}>{st.toUpperCase()}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDTShort(m.start_at)} · {realizados.length}/{(m.services || []).length} realizados · <strong>{total.toFixed(2)}€</strong>
                    {m.holded_presupuesto_id && <span className="ml-2 text-green-600 font-semibold">✓ Holded{m.holded_mock ? " (sim)" : ""}</span>}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button type="button" onClick={() => nav(`/trabajos/${m.id}`)} title="Ver" className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">👁</button>
                  <button type="button" onClick={() => nav(`/editar-mantenimiento/${m.id}`)} title="Editar" className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Edit2 size={14} /></button>
                  <button type="button" onClick={() => del(m.id)} title="Eliminar" className="w-9 h-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {holdedTarget && (
        <HoldedModal
          mantenimiento={holdedTarget}
          embarcacion={embMap[holdedTarget.embarcacion_id]}
          servMap={servMap}
          onClose={() => setHoldedTarget(null)}
          onDone={(result) => {
            setHoldedTarget(null);
            setMsg({
              type: "ok",
              title: `✓ Presupuesto ${result.mock ? "(SIMULACIÓN)" : ""} creado: ${result.document_number || result.holded_id}`,
              body: `${result.items_count} líneas · Subtotal ${result.subtotal}€ · IVA ${result.tax}€ · Total ${result.total}€`,
            });
            reload();
          }}
          onError={(err) => setMsg({ type: "err", title: "Error al emitir presupuesto", body: err })}
        />
      )}
    </div>
  );
}

function HoldedModal({ mantenimiento, embarcacion, servMap, onClose, onDone, onError }) {
  const [taxRate, setTaxRate] = useState(21);
  const [onlyRealizados, setOnlyRealizados] = useState(true);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/holded/status").then((r) => setMode(r.data.mode)).catch(() => setMode("mock"));
  }, []);

  const services = (mantenimiento.services || []).filter((s) => !onlyRealizados || s.realizado);
  const subtotal = services.reduce((acc, s) => acc + (servMap[s.service_id]?.price || 0), 0);
  const taxTotal = subtotal * (taxRate / 100);
  const total = subtotal + taxTotal;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mantenimientos/${mantenimiento.id}/holded-presupuesto`, {
        tax_rate: taxRate, only_realizados: onlyRealizados,
      });
      onDone(data);
    } catch (ex) { onError(fmtErr(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="holded-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="sns-title text-xl">Emitir presupuesto Holded</h3>
          {mode === "mock" && <span className="sns-pill bg-yellow-100 text-yellow-800 text-[10px]">MODO SIMULACIÓN</span>}
        </div>
        <p className="text-sm text-slate-500 mb-4">
          <strong>{embarcacion?.name}</strong> · {formatDTShort(mantenimiento.start_at)}
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-2 block">Tipo de IVA</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setTaxRate(21)} className={`p-3 rounded-xl border-2 text-sm font-semibold ${taxRate === 21 ? "border-[var(--coral)] bg-orange-50" : "border-[var(--border)] bg-white"}`} data-testid="tax-21">
                <div className="text-lg sns-title">21%</div><div className="text-xs text-slate-500">IVA general</div>
              </button>
              <button type="button" onClick={() => setTaxRate(0)} className={`p-3 rounded-xl border-2 text-sm font-semibold ${taxRate === 0 ? "border-[var(--coral)] bg-orange-50" : "border-[var(--border)] bg-white"}`} data-testid="tax-0">
                <div className="text-lg sns-title">0%</div><div className="text-xs text-slate-500">Intracomunitario</div>
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onlyRealizados} onChange={(e) => setOnlyRealizados(e.target.checked)} className="w-5 h-5 accent-[var(--coral)]" data-testid="only-realizados" />
            Solo servicios realizados ({(mantenimiento.services || []).filter((s) => s.realizado).length}/{(mantenimiento.services || []).length})
          </label>

          <div className="sns-card bg-slate-50 p-4">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Resumen</div>
            <div className="space-y-1 text-sm mb-3">
              {services.map((s) => {
                const sv = servMap[s.service_id];
                if (!sv) return null;
                return (
                  <div key={s.service_id} className="flex justify-between">
                    <span className="truncate mr-2">{sv.code} {sv.name}</span>
                    <span className="tabular-nums">{sv.price.toFixed(2)}€</span>
                  </div>
                );
              })}
              {services.length === 0 && <p className="text-center text-red-500 italic">No hay servicios que incluir.</p>}
            </div>
            <div className="border-t border-[var(--border)] pt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><strong className="tabular-nums">{subtotal.toFixed(2)}€</strong></div>
              <div className="flex justify-between text-slate-500"><span>IVA ({taxRate}%)</span><span className="tabular-nums">{taxTotal.toFixed(2)}€</span></div>
              <div className="flex justify-between sns-title text-lg"><span>Total</span><span className="tabular-nums text-[var(--coral)]">{total.toFixed(2)}€</span></div>
            </div>
          </div>

          {mode === "mock" && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs rounded-xl p-3">
              <strong>Simulación activa.</strong> Los presupuestos se crean internamente sin tocar tu cuenta Holded real. Para conectar Holded en producción, edita <code>/app/backend/.env</code>: pon <code>HOLDED_MODE="real"</code> y añade tu <code>HOLDED_API_KEY</code>.
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button type="button" onClick={submit} disabled={busy || services.length === 0} className="sns-btn sns-btn-accent flex-1" data-testid="confirm-holded">
            {busy ? "Emitiendo..." : "Emitir presupuesto"}
          </button>
        </div>
      </div>
    </div>
  );
}

const formatDTShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};

const Inp = ({ label, v, s, type = "text", required, disabled }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}{required && " *"}</label>
    <input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)} required={required} disabled={disabled} className="sns-input" />
  </div>
);

// ---------------- Servicios ----------------
export function GestionServicios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [query, setQuery] = useState("");

  const reload = async () => {
    setLoading(true);
    try { const { data } = await api.get("/servicios", { params: query ? { q: query } : {} }); setItems(data); } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [query]);

  const del = async (id) => { if (!confirm("¿Eliminar servicio?")) return; await api.delete(`/servicios/${id}`); reload(); };

  return (
    <div className="px-5 pt-8 pb-32 max-w-4xl mx-auto" data-testid="page-servicios">
      <AdminHeader title="Servicios" onNew={() => setShowForm({})} />
      <FiltersBar filter="" setFilter={() => {}} query={query} setQuery={setQuery} withActive={false} />
      {loading ? <Spinner /> : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="sns-card p-4 flex items-center gap-3" data-testid={`servicio-${s.id}`}>
              <span className="sns-pill bg-[var(--sky)] text-[var(--sky-text)] shrink-0">{s.code || "—"}</span>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{s.name}</div>
                <div className="text-xs text-slate-500">{s.especial && "Servicio especial · "}{s.price}€</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowForm(s)} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Edit2 size={14} /></button>
                <button onClick={() => del(s.id)} className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showForm !== null && <SimpleCrudModal title="Servicio" initial={showForm} onClose={() => setShowForm(null)}
        fields={[
          { k: "code", label: "Código (S1, SE2...)" },
          { k: "name", label: "Nombre", required: true },
          { k: "price", label: "Precio (€)", type: "number", step: "0.01" },
          { k: "especial", label: "Servicio especial", type: "checkbox" },
        ]}
        onSave={async (d) => {
          d.price = parseFloat(d.price || 0);
          if (showForm.id) await api.patch(`/servicios/${showForm.id}`, d);
          else await api.post("/servicios", d);
          setShowForm(null); reload();
        }} />}
    </div>
  );
}

// ---------------- Usuarios ----------------
function InviteCodeCard() {
  const [code, setCode] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenant/invite-code");
      setCode(data.invite_code || "");
      setTenantName(data.tenant_name || "");
    } catch {
      /* non-fatal */
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const share = async () => {
    const msg = `Hola! Únete al equipo de ${tenantName} en Smile & Shine.\n\n` +
      `1) Descarga la app:\n   iPhone: https://apps.apple.com/es/app/id6803214279\n` +
      `   Android: https://play.google.com/store/apps/details?id=es.smileandshine.cleaning\n\n` +
      `2) En la app, pulsa "Unirse a una empresa" y usa este código:\n\n   ${code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Smile & Shine", text: msg });
      } else {
        await navigator.clipboard.writeText(msg);
        toast.success("Mensaje copiado para WhatsApp");
      }
    } catch { /* user cancelled */ }
  };

  if (loading) return null;

  const masked = code ? code.slice(0, 2) + "••••" : "";

  return (
    <div
      className="rounded-2xl p-5 mb-5 border"
      style={{ background: "linear-gradient(135deg,#E0F2FE 0%,#F0F9FF 100%)", borderColor: "#BAE6FD" }}
      data-testid="invite-code-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] tracking-wider font-bold text-sky-700 mb-1">CÓDIGO DE INVITACIÓN</div>
          <div className="text-xs text-slate-600 leading-snug">
            Compártelo con tus empleados. Ellos lo usan en <em>"Unirse a una empresa"</em> al instalar la app.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <div
          onClick={() => setReveal((v) => !v)}
          className="flex-1 bg-[#0F2A4D] text-sky-300 font-mono font-black text-2xl md:text-3xl tracking-[0.3em] rounded-xl py-3 text-center cursor-pointer select-none"
          data-testid="invite-code-value"
          title={reveal ? "Ocultar" : "Mostrar"}
        >
          {reveal ? code : masked}
        </div>
      </div>
      <button
        onClick={share}
        className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--sky-text)] text-white font-semibold text-sm active:scale-95 transition-transform"
        data-testid="btn-share-invite"
      >
        <Share2 size={16} /> Compartir código
      </button>
    </div>
  );
}

export function GestionUsuarios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [query, setQuery] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);
  const [toDelete, setToDelete] = useState(null);
  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Usuario desactivado" : "Usuario activado");
      reload();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const resetPwd = async (u) => {
    if (!confirm(`¿Generar nueva contraseña temporal para ${u.name}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      prompt(`Nueva contraseña (cópiala y pásasela a ${u.name}):`, data.password);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const fmtLast = (iso) => {
    if (!iso) return "nunca";
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "hace instantes";
      if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
      if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)} d`;
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
    } catch { return iso.slice(0, 10); }
  };

  const filtered = items.filter((u) => {
    if (filter === "true" && !u.active) return false;
    if (filter === "false" && u.active) return false;
    if (query && !(u.name?.toLowerCase().includes(query.toLowerCase()) || u.email?.toLowerCase().includes(query.toLowerCase()))) return false;
    return true;
  });

  return (
    <div className="px-5 pt-8 pb-32 max-w-4xl mx-auto" data-testid="page-usuarios">
      <AdminHeader title="Usuarios" onNew={() => setShowForm({ role: "empleado", active: true })} />
      <InviteCodeCard />
      <FiltersBar filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} />
      {loading ? <Spinner /> : (
        <div className="space-y-3">
          {filtered.map((u) => (
            <div key={u.id} className="sns-card p-4 flex items-center gap-3" data-testid={`usuario-${u.id}`}>
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[var(--ink)] text-white flex items-center justify-center font-bold">{u.name?.[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-bold truncate">{u.name}</div>
                  {!u.active && <span className="sns-pill bg-slate-200 text-slate-500 text-[10px]">INACTIVO</span>}
                  {u.role === "admin" && <span className="sns-pill bg-indigo-100 text-indigo-700 text-[10px]">ADMIN</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email} · {u.rate_per_hour}€/h</div>
                <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                  <Clock size={10} /> Último acceso: {fmtLast(u.last_login)}
                </div>
              </div>
              <div className="flex gap-1.5 flex-wrap justify-end">
                <button
                  onClick={() => toggleActive(u)}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${u.active ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100"}`}
                  title={u.active ? "Desactivar" : "Activar"}
                  data-testid={`btn-toggle-${u.id}`}
                >
                  <Power size={15} strokeWidth={2.5} />
                </button>
                <button onClick={() => resetPwd(u)} className="w-9 h-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center" title="Reset contraseña" data-testid={`btn-pwd-${u.id}`}>
                  <Key size={14} />
                </button>
                <button onClick={() => setShowForm({ ...u, password: "", password2: "" })} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center" title="Editar" data-testid={`btn-edit-${u.id}`}><Edit2 size={14} /></button>
                <button onClick={() => setToDelete(u)} className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-600" title="Eliminar" data-testid={`btn-delete-${u.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showForm !== null && <UsuarioModal initial={showForm} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); reload(); }} />}
      {toDelete && <DeleteUserModal target={toDelete} users={items} onClose={() => setToDelete(null)} onDeleted={() => { setToDelete(null); reload(); }} />}
    </div>
  );
}

function DeleteUserModal({ target, users, onClose, onDeleted }) {
  const [summary, setSummary] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const candidates = users.filter((u) => u.id !== target.id && u.active);

  useEffect(() => {
    api.get(`/users/${target.id}/trabajos-resumen`).then(({ data }) => setSummary(data)).catch((e) => setErr(fmtErr(e)));
  }, [target.id]);

  const confirmDelete = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/users/${target.id}`, { params: summary?.total ? { reassign_to: reassignTo } : {} });
      toast.success(`${target.name} eliminado`);
      onDeleted();
    } catch (e) { setErr(fmtErr(e)); } finally { setBusy(false); }
  };

  const needsReassign = (summary?.total || 0) > 0;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="modal-delete-user">
        <h3 className="sns-title text-xl mb-2">Eliminar a {target.name}</h3>
        <p className="text-sm text-slate-600 mb-4">Se borrarán su nombre, email y acceso a la app. Esta acción no se puede deshacer.</p>
        {!summary && !err && <Spinner />}
        {summary && needsReassign && (
          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-4" data-testid="delete-user-reassign">
            <div className="text-sm text-orange-900 mb-3">
              Tiene <strong>{summary.completed}</strong> trabajo{summary.completed !== 1 ? "s" : ""} realizado{summary.completed !== 1 ? "s" : ""}
              {summary.pending > 0 && <> y <strong>{summary.pending}</strong> pendiente{summary.pending !== 1 ? "s" : ""}</>}. ¿A quién se los asignamos?
            </div>
            <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} className="sns-input bg-white" data-testid="select-reassign-to">
              <option value="">Elige una persona…</option>
              {candidates.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role === "admin" ? " (admin)" : ""}</option>)}
            </select>
          </div>
        )}
        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mb-3" data-testid="delete-user-error">{err}</div>}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1" data-testid="btn-cancel-delete-user">Cancelar</button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={busy || !summary || (needsReassign && !reassignTo)}
            className="sns-btn flex-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="btn-confirm-delete-user"
          >
            {busy ? "Eliminando…" : "Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsuarioModal({ initial, onClose, onSaved }) {
  const [d, setD] = useState(initial);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [pendingDeactivate, setPendingDeactivate] = useState(null); // { count, items, payload }
  const editing = !!initial.id;
  const set = (k, v) => setD({ ...d, [k]: v });

  const onAvatarFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const r = new FileReader();
    r.onload = () => set("avatar_url", r.result);
    r.readAsDataURL(f);
  };

  const persistUpdate = async (payload) => {
    if (!payload.password) delete payload.password;
    delete payload.email;
    await api.patch(`/users/${initial.id}`, payload);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      // Nuevo empleado por invitación: no se piden ni contraseña ni precio/hora
      const isNewEmpleado = !editing && d.role === "empleado";
      if (!isNewEmpleado && !editing && (d.password || "") !== (d.password2 || "")) {
        setSaving(false); return setErr("Las contraseñas no coinciden");
      }
      const payload = { ...d }; delete payload.password2;
      if (isNewEmpleado) {
        delete payload.password;
        payload.rate_per_hour = 0;
      } else {
        payload.rate_per_hour = parseFloat(payload.rate_per_hour || 0);
      }

      // Caso especial: desactivar empleado existente — comprobar trabajos futuros
      const isDeactivating = editing && initial.active && !d.active && initial.role === "empleado";
      if (isDeactivating) {
        const { data } = await api.get(`/users/${initial.id}/trabajos-futuros`);
        if (data.count > 0) {
          setSaving(false);
          setPendingDeactivate({ count: data.count, items: data.items, payload });
          return;
        }
      }

      if (editing) {
        await persistUpdate(payload);
      } else {
        const { data } = await api.post("/users", payload);
        toast.success(`${data.name} creado (${data.email})`);
      }
      onSaved();
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };

  const confirmDeactivate = async (action) => {
    // action: "keep" | "unassign"
    if (!pendingDeactivate) return;
    setSaving(true);
    try {
      if (action === "unassign") {
        await api.post(`/users/${initial.id}/desasignar-trabajos-futuros`);
      }
      await persistUpdate(pendingDeactivate.payload);
      setPendingDeactivate(null);
      onSaved();
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") {
            e.preventDefault();
          }
        }}
        className="bg-white rounded-3xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="usuario-modal">
        <h3 className="sns-title text-xl mb-4">{editing ? "Editar usuario" : "Nuevo usuario"}</h3>

        <div className="flex items-center gap-4 mb-4">
          {d.avatar_url ? (
            <img src={d.avatar_url} alt="" className="w-20 h-20 rounded-full object-cover ring-2 ring-[var(--coral)]/30" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">?</div>
          )}
          <label className="sns-btn sns-btn-ghost bg-slate-100 cursor-pointer">
            Subir foto
            <input type="file" accept="image/*" onChange={onAvatarFile} className="hidden" data-testid="user-avatar-file" />
          </label>
          {d.avatar_url && (
            <button type="button" onClick={() => set("avatar_url", null)} className="text-xs text-red-600 font-semibold">Quitar</button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Inp label="Email" type="email" v={d.email} s={(v) => set("email", v)} required={!editing} disabled={editing} /></div>
          {/* Nuevo empleado por invitación: no se pide contraseña ni precio/hora */}
          {!(d.role === "empleado" && !editing) && (
            <>
              <Inp label={editing ? "Nueva contraseña (opcional)" : "Contraseña"} type="password" v={d.password} s={(v) => set("password", v)} required={!editing} />
              {!editing && <Inp label="Confirmar contraseña" type="password" v={d.password2} s={(v) => set("password2", v)} required />}
            </>
          )}
          <Inp label="Nombre" v={d.name} s={(v) => set("name", v)} required />
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo</label>
            <select value={d.role} onChange={(e) => set("role", e.target.value)} className="sns-input">
              <option value="empleado">Empleado</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          {!(d.role === "empleado" && !editing) && (
            <Inp label="Precio/hora (€)" type="number" v={d.rate_per_hour} s={(v) => set("rate_per_hour", v)} />
          )}
        </div>

        {d.role === "empleado" && !editing && (
          <div className="mt-4 bg-sky-50 border border-sky-200 rounded-xl p-3 text-sm text-sky-900">
            <strong>📧 Invitación por email:</strong> Se enviará un enlace a <strong>{d.email || "este email"}</strong> para que el empleado elija su propia contraseña. El precio/hora lo configuras después desde Ajustes.
          </div>
        )}
        <label className="inline-flex items-center gap-2 mt-3">
          <input type="checkbox" checked={!!d.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-[var(--coral)]" />
          <span className="text-sm">Usuario activo</span>
        </label>
        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mt-3">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button disabled={saving} className="sns-btn sns-btn-accent flex-1" data-testid="usuario-save">
            {saving ? "..." : (d.role === "empleado" && !editing ? "Enviar invitación" : "Guardar")}
          </button>
        </div>
      </form>

      {pendingDeactivate && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-end md:items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto" data-testid="modal-confirmar-desactivar">
            <h3 className="sns-title text-xl mb-2">⚠️ Trabajos futuros pendientes</h3>
            <p className="text-sm text-slate-600 mb-4">
              <strong>{initial.name}</strong> tiene <strong>{pendingDeactivate.count} trabajo{pendingDeactivate.count !== 1 ? "s" : ""}</strong> programado{pendingDeactivate.count !== 1 ? "s" : ""} para hoy o en el futuro:
            </p>
            <div className="bg-slate-50 rounded-xl p-3 mb-4 max-h-48 overflow-y-auto space-y-1">
              {pendingDeactivate.items.slice(0, 8).map((it) => (
                <div key={it.id} className="text-sm flex justify-between gap-2">
                  <span className="font-semibold truncate">{it.embarcacion_name}</span>
                  <span className="text-slate-500 text-xs whitespace-nowrap">
                    {new Date(it.start_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              ))}
              {pendingDeactivate.items.length > 8 && (
                <div className="text-xs text-slate-400 pt-1">+ {pendingDeactivate.items.length - 8} más…</div>
              )}
            </div>
            <p className="text-sm text-slate-700 mb-4">¿Qué quieres hacer con ellos?</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => confirmDeactivate("unassign")}
                disabled={saving}
                className="sns-btn sns-btn-accent w-full justify-center"
                data-testid="btn-desasignar"
              >
                Desasignar al empleado y desactivarlo
              </button>
              <button
                type="button"
                onClick={() => confirmDeactivate("keep")}
                disabled={saving}
                className="sns-btn sns-btn-ghost bg-slate-100 w-full justify-center"
                data-testid="btn-mantener-asignaciones"
              >
                Mantener asignaciones (solo desactivar usuario)
              </button>
              <button
                type="button"
                onClick={() => setPendingDeactivate(null)}
                disabled={saving}
                className="sns-btn sns-btn-ghost text-slate-500 w-full justify-center"
              >
                Cancelar
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Nota: al desasignar, los mantenimientos se mantienen pero sin este empleado. Podrás reasignarlos manualmente.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Embarcaciones con checkouts ----------------
export function GestionEmbarcaciones() {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [query, setQuery] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "todos") params.active = filter;
      if (query) params.q = query;
      const [e, c] = await Promise.all([api.get("/embarcaciones", { params }), api.get("/clientes")]);
      setItems(e.data); setClientes(c.data);
    } finally { setLoading(false); }
  };
  const reloadClientes = async () => {
    const { data } = await api.get("/clientes");
    setClientes(data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter, query]);
  const del = async (id) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/embarcaciones/${id}`); reload(); };

  return (
    <div className="px-5 pt-8 pb-32 max-w-4xl mx-auto" data-testid="page-gestion-embarcaciones">
      <AdminHeader title="Chalets / Embarcaciones" onNew={() => setShowForm({ tipo: "embarcacion", active: true, checkouts: [] })} />
      <FiltersBar filter={filter} setFilter={setFilter} query={query} setQuery={setQuery} />
      {loading ? <Spinner /> : (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} className="sns-card p-4 flex items-center gap-3">
              {e.photo_url && <img src={e.photo_url} alt="" className="w-14 h-14 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{e.name}</div>
                <div className="text-xs text-slate-500 truncate">{e.tipo} · {e.puerto || "—"} · {e.checkouts?.length || 0} zonas</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowForm({ ...e, checkouts: e.checkouts || [] })} className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Edit2 size={14} /></button>
                <button onClick={() => del(e.id)} className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showForm !== null && <EmbarcacionModal initial={showForm} clientes={clientes} onClose={() => setShowForm(null)} onSaved={() => { setShowForm(null); reload(); }} onClientesChange={reloadClientes} />}
    </div>
  );
}

function EmbarcacionModal({ initial, clientes, onClose, onSaved, onClientesChange }) {
  const [d, setD] = useState(initial);
  const [newCheck, setNewCheck] = useState("");
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("data");
  // Inline client creation
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ empresa: "", nombre: "", email: "", telefono: "" });
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientErr, setClientErr] = useState("");
  const set = (k, v) => setD({ ...d, [k]: v });

  const createClientInline = async () => {
    setClientErr("");
    if (!newClient.empresa.trim() && !newClient.nombre.trim()) {
      setClientErr("Introduce al menos la empresa o el nombre");
      return;
    }
    setCreatingClient(true);
    try {
      const { data: created } = await api.post("/clientes", {
        empresa: newClient.empresa.trim() || newClient.nombre.trim(),
        nombre: newClient.nombre.trim(),
        email: newClient.email.trim(),
        telefono: newClient.telefono.trim(),
        active: true,
      });
      // Notify parent so clientes list refreshes
      if (onClientesChange) await onClientesChange();
      // Auto-select newly created client
      set("cliente_id", created.id);
      // Reset & close inline form
      setNewClient({ empresa: "", nombre: "", email: "", telefono: "" });
      setShowNewClient(false);
      toast.success(`Cliente "${created.empresa || created.nombre}" creado`);
    } catch (ex) {
      setClientErr(fmtErr(ex));
    } finally {
      setCreatingClient(false);
    }
  };

  const addCheckout = () => {
    if (!newCheck.trim()) return;
    set("checkouts", [...(d.checkouts || []), newCheck.trim()]);
    setNewCheck("");
  };
  const rmCheckout = (i) => {
    set("checkouts", (d.checkouts || []).filter((_, j) => j !== i));
    if (editingIdx === i) { setEditingIdx(null); setEditingText(""); }
  };
  const startEdit = (i) => { setEditingIdx(i); setEditingText(d.checkouts[i] || ""); };
  const saveEdit = () => {
    if (editingIdx == null) return;
    if (!editingText.trim()) return;
    const arr = [...(d.checkouts || [])];
    arr[editingIdx] = editingText.trim();
    set("checkouts", arr);
    setEditingIdx(null); setEditingText("");
  };
  const cancelEdit = () => { setEditingIdx(null); setEditingText(""); };
  const moveCheckout = (i, dir) => {
    const arr = [...(d.checkouts || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set("checkouts", arr);
  };

  const onPhotoFile = async (e) => {
    const input = e.target;
    const f = input.files?.[0];
    input.value = "";   // permite volver a elegir el mismo archivo
    if (!f) return;
    try {
      // Miniatura de propiedad: 800px / JPEG 80% (~100 KB en vez de varios MB)
      set("photo_url", await fileToResizedDataUrl(f, { maxSize: 800, quality: 0.8 }));
    } catch {
      toast.error("No se pudo procesar la imagen. Prueba con otra foto (JPG o PNG).");
    }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      if (initial.id) await api.patch(`/embarcaciones/${initial.id}`, d);
      else await api.post("/embarcaciones", d);
      onSaved();
    } catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          // Prevent Enter in any input (not textarea) from submitting the whole form.
          // User still has to click the "Guardar" button at the bottom.
          if (e.key === "Enter" && e.target.tagName === "INPUT" && e.target.type !== "submit") {
            e.preventDefault();
          }
        }}
        className="bg-white rounded-3xl w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="emb-modal">
        <h3 className="sns-title text-xl mb-4">{initial.id ? "Editar" : "Nueva"} {d.tipo === "chalet" ? "chalet" : "embarcación"}</h3>

        <div className="flex gap-2 mb-4 border-b border-[var(--border)]">
          {[
            ["data", "Datos", null],
            ["checkouts", "Checklist", (d.checkouts || []).length],
            ["notas", "Notas", null],
          ].map(([k, l, count]) => (
            <button type="button" key={k} onClick={() => setTab(k)}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${tab === k ? "border-[var(--coral)] text-[var(--ink)]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                    data-testid={`tab-${k}`}>
              {l}
              {count != null && count > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tab === k ? "bg-[var(--coral)] text-white" : "bg-slate-200 text-slate-600"}`}>{count}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "data" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Inp label="Nombre" v={d.name} s={(v) => set("name", v)} required /></div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Tipo</label>
                <select value={d.tipo} onChange={(e) => set("tipo", e.target.value)} className="sns-input">
                  <option value="embarcacion">Embarcación</option>
                  <option value="chalet">Chalet</option>
                </select>
              </div>
              <Inp label="Matrícula" v={d.matricula} s={(v) => set("matricula", v)} />
              <div className="col-span-2"><Inp label="Descripción" v={d.description} s={(v) => set("description", v)} /></div>
              <div className="col-span-2"><Inp label="Puerto / Ubicación" v={d.puerto} s={(v) => set("puerto", v)} /></div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-600">Cliente *</label>
                  <button
                    type="button"
                    onClick={() => setShowNewClient((v) => !v)}
                    className="text-xs font-bold text-[var(--coral)] hover:underline flex items-center gap-1"
                    data-testid="btn-new-client-inline"
                  >
                    <Plus size={12} /> {showNewClient ? "Cancelar" : "Nuevo cliente"}
                  </button>
                </div>
                <select value={d.cliente_id || ""} onChange={(e) => set("cliente_id", e.target.value)} required={!showNewClient} className="sns-input" data-testid="emb-cliente">
                  <option value="">Selecciona...</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.empresa || c.nombre}</option>)}
                </select>

                {showNewClient && (
                  <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-3" data-testid="inline-new-client">
                    <div className="text-xs font-bold text-sky-800 tracking-wider">
                      ➕ CREAR NUEVO CLIENTE
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Inp label="Empresa *" v={newClient.empresa} s={(v) => setNewClient({ ...newClient, empresa: v })} />
                      <Inp label="Persona contacto" v={newClient.nombre} s={(v) => setNewClient({ ...newClient, nombre: v })} />
                      <Inp label="Email" type="email" v={newClient.email} s={(v) => setNewClient({ ...newClient, email: v })} />
                      <Inp label="Teléfono" v={newClient.telefono} s={(v) => setNewClient({ ...newClient, telefono: v })} />
                    </div>
                    {clientErr && <div className="text-xs text-red-600">{clientErr}</div>}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowNewClient(false); setClientErr(""); }}
                        className="sns-btn sns-btn-ghost bg-white text-sm flex-1"
                      >Cancelar</button>
                      <button
                        type="button"
                        onClick={createClientInline}
                        disabled={creatingClient}
                        className="sns-btn sns-btn-accent text-sm flex-1 disabled:opacity-50"
                        data-testid="btn-save-inline-client"
                      >
                        {creatingClient ? "Creando..." : "Crear y seleccionar"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Foto</label>
              <div className="flex items-center gap-3">
                {d.photo_url && <img src={d.photo_url} alt="" className="w-20 h-20 rounded-xl object-cover" />}
                <label className="sns-btn sns-btn-ghost bg-slate-100 cursor-pointer">
                  {d.photo_url ? "Cambiar foto" : "Subir foto"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhotoFile} />
                </label>
                {d.photo_url && <button type="button" onClick={() => set("photo_url", null)} className="text-red-600 text-xs font-semibold">Quitar</button>}
              </div>
              <div className="mt-2"><Inp label="O URL de imagen" v={d.photo_url?.startsWith("http") ? d.photo_url : ""} s={(v) => set("photo_url", v)} /></div>
            </div>
            <label className="inline-flex items-center gap-2 mt-3">
              <input type="checkbox" checked={!!d.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-[var(--coral)]" />
              <span className="text-sm">Activa</span>
            </label>

            {!initial.id && (d.checkouts || []).length === 0 && (
              <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="text-xl leading-none">💡</div>
                <div className="text-sm text-amber-900 flex-1">
                  <strong>Añade una checklist</strong> con las zonas que el empleado tendrá que revisar (cubierta, cocina, baño...). Lo haces en la pestaña{" "}
                  <button type="button" onClick={() => setTab("checkouts")} className="font-bold underline text-[var(--coral)]">Checklist</button>.
                </div>
              </div>
            )}
          </>
        )}

        {tab === "checkouts" && (
          <div>
            <p className="text-sm text-slate-500 mb-3">Checklist de zonas/áreas que el empleado deberá marcar antes de finalizar el mantenimiento. Añade todas las zonas de la propiedad.</p>
            <div className="flex gap-2 mb-3">
              <input value={newCheck} onChange={(e) => setNewCheck(e.target.value)}
                     placeholder="Ej: Cubierta, Cocina, Baño 1..."
                     className="sns-input flex-1" data-testid="new-checkout-input"
                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCheckout(); } }} />
              <button type="button" onClick={addCheckout} className="sns-btn sns-btn-accent" data-testid="btn-add-checkout">Añadir</button>
            </div>
            <div className="space-y-1">
              {(d.checkouts || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl bg-slate-50" data-testid={`checkpoint-row-${i}`}>
                  <span className="text-xs font-bold text-slate-400 w-6 text-center tabular-nums">{i + 1}</span>
                  {editingIdx === i ? (
                    <>
                      <input
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
                          if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                        }}
                        className="sns-input flex-1 text-sm"
                        autoFocus
                        data-testid={`checkpoint-edit-input-${i}`}
                      />
                      <button type="button" onClick={saveEdit} className="text-emerald-700 text-xs font-semibold px-2" data-testid={`btn-save-checkpoint-${i}`}>Guardar</button>
                      <button type="button" onClick={cancelEdit} className="text-slate-500 text-xs font-semibold px-2">Cancelar</button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm">{c}</span>
                      <button type="button" onClick={() => moveCheckout(i, -1)} disabled={i === 0}
                              className="text-slate-400 text-xs px-1 disabled:opacity-30" aria-label="Subir">▲</button>
                      <button type="button" onClick={() => moveCheckout(i, 1)} disabled={i === (d.checkouts.length - 1)}
                              className="text-slate-400 text-xs px-1 disabled:opacity-30" aria-label="Bajar">▼</button>
                      <button type="button" onClick={() => startEdit(i)} className="text-[var(--coral)] text-xs font-semibold px-2" data-testid={`btn-edit-checkpoint-${i}`}>Editar</button>
                      <button type="button" onClick={() => rmCheckout(i)} className="text-red-500 text-xs font-semibold px-2" data-testid={`btn-rm-checkpoint-${i}`}>Quitar</button>
                    </>
                  )}
                </div>
              ))}
              {(!d.checkouts || d.checkouts.length === 0) && (
                <p className="text-xs text-slate-400 italic text-center py-4">Aún no hay checkpoints. Añade el primero arriba.</p>
              )}
            </div>
          </div>
        )}

        {tab === "notas" && (
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Notas internas sobre la embarcación</label>
            <textarea value={d.notas || ""} onChange={(e) => set("notas", e.target.value)} rows={8} className="sns-input resize-none" placeholder="Observaciones, contactos, particularidades..." />
          </div>
        )}

        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mt-3">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button disabled={saving} className="sns-btn sns-btn-accent flex-1" data-testid="emb-save">{saving ? "..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Albaranes ----------------
export function GestionAlbaranes() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(todayStr);
  const [clienteFilter, setClienteFilter] = useState("");
  const [clientes, setClientes] = useState([]);
  const [holdedTarget, setHoldedTarget] = useState(null); // { item, embarcacion }
  const [embMap, setEmbMap] = useState({});
  const [servMap, setServMap] = useState({});
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (clienteFilter) params.cliente_id = clienteFilter;
      const [a, c, e, s] = await Promise.all([
        api.get("/albaranes", { params }),
        api.get("/clientes"),
        api.get("/embarcaciones"),
        api.get("/servicios"),
      ]);
      setData(a.data);
      setClientes(c.data);
      setEmbMap(Object.fromEntries(e.data.map((x) => [x.id, x])));
      setServMap(Object.fromEntries(s.data.map((x) => [x.id, x])));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setRange = (preset) => {
    const t = new Date();
    if (preset === "month") {
      setFrom(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10));
      setTo(t.toISOString().slice(0, 10));
    } else if (preset === "lastMonth") {
      const f = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const l = new Date(t.getFullYear(), t.getMonth(), 0);
      setFrom(f.toISOString().slice(0, 10));
      setTo(l.toISOString().slice(0, 10));
    } else if (preset === "year") {
      setFrom(`${t.getFullYear()}-01-01`);
      setTo(t.toISOString().slice(0, 10));
    }
  };

  return (
    <div className="px-5 pt-8 pb-32 max-w-4xl mx-auto" data-testid="page-albaranes">
      <AdminHeader title="Albaranes" />

      <div className="sns-card p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-bold">Periodo</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button onClick={() => setRange("month")} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-semibold">Mes en curso</button>
          <button onClick={() => setRange("lastMonth")} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-semibold">Mes pasado</button>
          <button onClick={() => setRange("year")} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 font-semibold">Año en curso</button>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="sns-input" data-testid="alb-from" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="sns-input" data-testid="alb-to" />
          </div>
        </div>
        <div className="mb-3">
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Cliente (opcional)</label>
          <select value={clienteFilter} onChange={(e) => setClienteFilter(e.target.value)} className="sns-input" data-testid="alb-cliente">
            <option value="">Todos los clientes</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.empresa || c.nombre}</option>)}
          </select>
        </div>
        <button onClick={load} className="sns-btn sns-btn-accent w-full" data-testid="btn-buscar-albaranes">
          Buscar
        </button>
      </div>

      {msg && (
        <div className={`text-sm rounded-xl p-3 mb-3 ${msg.type === "ok" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          <div className="font-bold mb-1">{msg.title}</div>
          <div className="text-xs whitespace-pre-wrap">{msg.body}</div>
        </div>
      )}

      {loading ? <Spinner /> : !data || data.grupos.length === 0 ? (
        <div className="sns-card p-10 text-center text-slate-400">Sin trabajos finalizados en este rango.</div>
      ) : (
        <>
          {/* Resumen global */}
          <div className="sns-card p-4 mb-4 bg-gradient-to-br from-[var(--ink)] to-[#1d3557] text-white" data-testid="alb-totales">
            <div className="text-xs uppercase tracking-wider opacity-70 mb-2">Total del periodo</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-2xl font-extrabold">{data.total_hours}h</div>
                <div className="text-xs opacity-70">Horas</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold">{data.total_services}</div>
                <div className="text-xs opacity-70">Trabajos</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold">{data.total_amount.toFixed(2)}€</div>
                <div className="text-xs opacity-70">Importe</div>
              </div>
            </div>
            {data.total_paused > 0 && (
              <div className="text-center text-xs opacity-70 mt-2">+ {data.total_paused}h en pausas</div>
            )}
          </div>

          <div className="space-y-4">
            {data.grupos.map((g) => (
              <AlbaranGrupo
                key={g.cliente_id || "_none"}
                g={g}
                dateFrom={data.date_from}
                dateTo={data.date_to}
                onEmitirHolded={(grupo, ctx) => setHoldedTarget({ grupo, ...ctx })}
              />
            ))}
          </div>
        </>
      )}

      {holdedTarget && (
        <HoldedAlbaranModal
          grupo={holdedTarget.grupo}
          dateFrom={holdedTarget.dateFrom}
          dateTo={holdedTarget.dateTo}
          importePendiente={holdedTarget.importePendiente}
          pendientes={holdedTarget.pendientes}
          onClose={() => setHoldedTarget(null)}
          onDone={(result) => {
            setHoldedTarget(null);
            setMsg({
              type: "ok",
              title: `✓ Presupuesto ${result.mock ? "(SIMULACIÓN)" : ""} creado: ${result.document_number || result.holded_id}`,
              body: `${result.mantenimientos_count} trabajos · ${result.items_count} servicios · Subtotal ${result.subtotal}€ · IVA ${result.tax}€ · Total ${result.total}€`,
            });
            load();
          }}
          onError={(err) => setMsg({ type: "err", title: "Error al emitir presupuesto", body: err })}
        />
      )}
    </div>
  );
}

const AlbaranGrupo = ({ g, onEmitirHolded, dateFrom, dateTo }) => {
  const [open, setOpen] = useState(true);
  const totalItems = g.items.length;
  const facturados = g.items.filter((it) => it.holded_id).length;
  const pendientes = totalItems - facturados;
  const allFacturados = facturados > 0 && pendientes === 0;
  // total a facturar SOLO de pendientes (los facturados ya no se incluyen)
  const importePendiente = g.items.filter((it) => !it.holded_id).reduce((acc, it) => acc + (it.total_amount || 0), 0);

  return (
    <div className="sns-card overflow-hidden" data-testid={`grupo-${g.cliente_id || "none"}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="text-left min-w-0 flex-1">
          <div className="font-bold truncate">{g.cliente_name}</div>
          <div className="text-xs text-slate-500 truncate">
            {g.total_services} trabajo{g.total_services !== 1 ? "s" : ""} · {g.total_hours}h
            {facturados > 0 && (
              <> · <span className="text-green-600 font-semibold">{facturados} ya facturado{facturados !== 1 ? "s" : ""}</span></>
            )}
          </div>
        </div>
        <div className="text-right shrink-0 ml-3">
          <div className="font-extrabold text-[var(--coral)] tabular-nums">{g.total_amount.toFixed(2)}€</div>
          <div className="text-[10px] text-slate-400">{open ? "▲ Ocultar" : "▼ Ver"}</div>
        </div>
      </button>
      {open && (
        <>
          <div className="border-t border-[var(--border)] divide-y divide-[var(--border)]">
            {g.items.map((it) => (
              <AlbaranItem key={it.id} item={it} />
            ))}
          </div>

          {/* Botón único agrupado */}
          <div className="border-t border-[var(--border)] bg-slate-50 p-4">
            {allFacturados ? (
              <div className="text-center text-sm font-semibold text-green-700 py-2" data-testid={`grupo-facturado-${g.cliente_id || "none"}`}>
                ✓ Todos los trabajos enviados a Holded
              </div>
            ) : g.cliente_id ? (
              <button
                type="button"
                onClick={() => onEmitirHolded(g, { dateFrom, dateTo, importePendiente, pendientes })}
                disabled={pendientes === 0}
                className="sns-btn sns-btn-accent w-full disabled:opacity-50"
                data-testid={`btn-emit-grupo-${g.cliente_id}`}
              >
                Emitir presupuesto en Holded · {pendientes} trabajo{pendientes !== 1 ? "s" : ""} pendiente{pendientes !== 1 ? "s" : ""} · {importePendiente.toFixed(2)}€
              </button>
            ) : (
              <div className="text-center text-xs text-slate-400 py-2">
                Sin cliente asignado · no se puede facturar
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const AlbaranItem = ({ item }) => {
  const [showMore, setShowMore] = useState(false);
  const t = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div className="p-4" data-testid={`alb-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate flex items-center gap-2">
            {item.embarcacion_name}
            {item.holded_id && (
              <span className="sns-pill bg-green-100 text-green-800 text-[10px]" data-testid={`pill-facturado-${item.id}`}>
                ✓ FACTURADO
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {t(item.checkin_at || item.fecha)} → {t(item.checkout_at)}
            {item.matricula && <> · {item.matricula}</>}
          </div>
          <div className="text-xs mt-1 flex flex-wrap items-center gap-2">
            <span className="font-semibold text-emerald-700">{item.hours_worked}h trabajadas</span>
            {item.paused_hours > 0 && <span className="text-amber-700">+ {item.paused_hours}h pausa</span>}
            {item.empleados.length > 0 && <span className="text-slate-500">· {item.empleados.join(", ")}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-bold tabular-nums">{item.total_amount.toFixed(2)}€</div>
          {item.holded_id && (
            <div className="text-[10px] text-green-600 font-semibold mt-0.5">
              {item.holded_mock ? "Holded (sim)" : "Holded ✓"}
            </div>
          )}
        </div>
      </div>

      {(item.servicios.length > 0 || item.observaciones || item.incidencias) && (
        <button type="button" onClick={() => setShowMore(!showMore)} className="text-xs text-[var(--coral)] font-semibold mt-2">
          {showMore ? "Ocultar detalles" : "Ver detalles"}
        </button>
      )}

      {showMore && (
        <div className="mt-3 space-y-2 pl-3 border-l-2 border-[var(--coral)]/30">
          {item.servicios.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Servicios · {item.hours_worked}h × precio</div>
              <div className="flex flex-wrap gap-1">
                {item.servicios.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-md bg-slate-100">
                    {s.name} · {s.price}€/h × {item.hours_worked}h = <strong>{(s.price * item.hours_worked).toFixed(2)}€</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
          {item.observaciones && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Observaciones</div>
              <div className="text-xs bg-slate-50 p-2 rounded-md whitespace-pre-wrap">{item.observaciones}</div>
            </div>
          )}
          {item.incidencias && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">⚠ Incidencias</div>
              <div className="text-xs bg-amber-50 p-2 rounded-md whitespace-pre-wrap">{item.incidencias}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function HoldedAlbaranModal({ grupo, dateFrom, dateTo, importePendiente, pendientes, onClose, onDone, onError }) {
  const [taxRate, setTaxRate] = useState(21);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);

  useEffect(() => {
    api.get("/holded/status").then((r) => setMode(r.data.mode)).catch(() => setMode("mock"));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/albaranes/holded-presupuesto-grupo`, {
        cliente_id: grupo.cliente_id,
        date_from: dateFrom,
        date_to: dateTo,
        tax_rate: parseFloat(taxRate),
      });
      onDone(data);
    } catch (ex) {
      onError(fmtErr(ex));
    } finally { setBusy(false); }
  };

  const tax = parseFloat(taxRate) || 0;
  const taxAmount = importePendiente * tax / 100;
  const total = importePendiente + taxAmount;
  // Lista de servicios pendientes para mostrar en el modal (precio × horas trabajadas)
  const pendingItems = grupo.items.filter((it) => !it.holded_id);
  const allServicios = pendingItems.flatMap((it) =>
    it.servicios.map((s) => ({
      ...s,
      embarcacion: it.embarcacion_name,
      fecha: it.fecha,
      hours: it.hours_worked || 0,
      lineTotal: (s.price || 0) * (it.hours_worked || 0),
    }))
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="holded-modal">
        <h3 className="sns-title text-xl mb-1">Emitir presupuesto en Holded</h3>
        <div className="text-sm text-slate-500 mb-4">{grupo.cliente_name}</div>

        <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm">
          <div className="font-semibold mb-2">
            {pendientes} trabajo{pendientes !== 1 ? "s" : ""} · {allServicios.length} servicio{allServicios.length !== 1 ? "s" : ""}
          </div>
          <div className="text-xs text-slate-500 mb-2">Periodo: {dateFrom} → {dateTo}</div>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {allServicios.slice(0, 12).map((s, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span className="truncate">{s.embarcacion} · {s.name} · {s.hours}h × {s.price.toFixed(2)}€</span>
                <span className="tabular-nums shrink-0 ml-2">{s.lineTotal.toFixed(2)}€</span>
              </div>
            ))}
            {allServicios.length > 12 && (
              <div className="text-xs text-slate-400 pt-1">+ {allServicios.length - 12} más…</div>
            )}
          </div>
        </div>

        <label className="text-xs font-semibold text-slate-600 mb-1 block">Régimen fiscal</label>
        <select value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="sns-input mb-4" data-testid="holded-tax">
          <option value={21}>IVA 21% (España)</option>
          <option value={0}>Intracomunitario · IVA 0%</option>
        </select>

        <div className="bg-[var(--sky)] rounded-xl p-3 mb-4 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{importePendiente.toFixed(2)}€</span></div>
          <div className="flex justify-between"><span>IVA ({tax}%)</span><span className="tabular-nums">{taxAmount.toFixed(2)}€</span></div>
          <div className="flex justify-between font-extrabold mt-1 pt-1 border-t border-[var(--border)]"><span>Total</span><span className="tabular-nums">{total.toFixed(2)}€</span></div>
        </div>

        {mode === "mock" && (
          <div className="text-xs text-amber-800 bg-amber-50 rounded-xl p-3 mb-4">
            <strong>Simulación activa.</strong> El presupuesto se crea internamente sin tocar Holded real.
          </div>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button type="button" onClick={submit} disabled={busy || allServicios.length === 0} className="sns-btn sns-btn-accent flex-1" data-testid="confirm-holded">
            {busy ? "Enviando..." : "Emitir"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- SimpleCrudModal (para servicios) ----------------
function SimpleCrudModal({ title, initial = {}, fields, onClose, onSave }) {
  const [data, setData] = useState(initial);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try { await onSave(data); }
    catch (ex) { setErr(fmtErr(ex)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="sns-title text-xl mb-4">{initial.id ? "Editar" : "Nuevo"} {title}</h3>
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.k}>
              <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</label>
              {f.type === "checkbox" ? (
                <label className="inline-flex items-center gap-2 py-2">
                  <input type="checkbox" checked={!!data[f.k]} onChange={(e) => setData({ ...data, [f.k]: e.target.checked })} className="w-5 h-5 accent-[var(--coral)]" />
                  <span className="text-sm">Activar</span>
                </label>
              ) : (
                <input type={f.type || "text"} step={f.step} required={f.required}
                       value={data[f.k] ?? ""} onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
                       className="sns-input" />
              )}
            </div>
          ))}
        </div>
        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mt-3">{err}</div>}
        <div className="flex gap-2 mt-5">
          <button type="button" onClick={onClose} className="sns-btn sns-btn-ghost bg-slate-100 flex-1">Cancelar</button>
          <button disabled={saving} className="sns-btn sns-btn-accent flex-1" data-testid="crud-save">{saving ? "..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------- AJUSTES (empresa + Holded + idioma) ----------------
import { useLang } from "@/i18n/LangContext";

export function GestionAjustes() {
  const { lang, setLang, t } = useLang();
  const [tab, setTab] = useState("empresa");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (ex) { setMsg({ type: "err", text: fmtErr(ex) }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="px-5 pt-8 pb-32 max-w-3xl mx-auto" data-testid="page-ajustes">
      <AdminHeader title={t("settings.title")} />

      {/* Tabs */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto no-scrollbar">
        {[
          { key: "empresa", label: t("settings.tab.empresa") },
          { key: "holded", label: t("settings.tab.holded") },
          { key: "idioma", label: t("settings.tab.idioma") },
        ].map((x) => (
          <button
            key={x.key}
            onClick={() => setTab(x.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
              tab === x.key ? "bg-[var(--ink)] text-white" : "bg-white text-slate-600 border border-[var(--border)]"
            }`}
            data-testid={`tab-${x.key}`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {msg && (
        <div className={`text-sm rounded-xl p-3 mb-3 ${msg.type === "ok" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="ajustes-msg">
          {msg.text}
        </div>
      )}

      {loading ? <Spinner /> : !settings ? null : (
        <>
          {tab === "empresa" && <EmpresaForm initial={settings.empresa} onSaved={(s) => { setMsg(s.msg); load(); }} t={t} />}
          {tab === "holded" && <HoldedForm initial={settings.holded} onSaved={(s) => { setMsg(s.msg); load(); }} t={t} />}
          {tab === "idioma" && <IdiomaForm lang={lang} setLang={setLang} t={t} />}
        </>
      )}
    </div>
  );
}

function EmpresaForm({ initial, onSaved, t }) {
  const [d, setD] = useState(initial || {});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setD({ ...d, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.put("/settings/empresa", d);
      onSaved({ msg: { type: "ok", text: t("settings.empresa.saved") } });
    } catch (ex) { onSaved({ msg: { type: "err", text: fmtErr(ex) } }); }
    finally { setSaving(false); }
  };

  const F = ({ k, label, ...rest }) => (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <input value={d[k] || ""} onChange={(e) => set(k, e.target.value)} className="sns-input" data-testid={`emp-${k}`} {...rest} />
    </div>
  );

  return (
    <form onSubmit={submit} className="sns-card p-5 space-y-4">
      <div>
        <h3 className="sns-title text-lg">{t("settings.empresa.title")}</h3>
        <p className="text-xs text-slate-500 mt-1">{t("settings.empresa.desc")}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F k="nombre" label={t("settings.empresa.nombre")} />
        <F k="cif" label={t("settings.empresa.cif")} />
        <div className="sm:col-span-2"><F k="direccion" label={t("settings.empresa.direccion")} /></div>
        <F k="cp" label={t("settings.empresa.cp")} />
        <F k="poblacion" label={t("settings.empresa.poblacion")} />
        <F k="provincia" label={t("settings.empresa.provincia")} />
        <F k="pais" label={t("settings.empresa.pais")} />
        <F k="telefono" label={t("settings.empresa.telefono")} />
        <F k="email" label={t("settings.empresa.email")} type="email" />
        <F k="apartado_correos" label={t("settings.empresa.apartado")} />
        <F k="web" label={t("settings.empresa.web")} placeholder="https://" />
      </div>
      <button disabled={saving} className="sns-btn sns-btn-accent" data-testid="btn-save-empresa">
        {saving ? t("common.saving") : t("common.save")}
      </button>
    </form>
  );
}

function HoldedForm({ initial, onSaved, t }) {
  const [d, setD] = useState({
    api_key: "",
    base_url: initial?.base_url || "https://api.holded.com/api/invoicing/v1",
    mode: initial?.mode || "mock",
    default_tax_rate: initial?.default_tax_rate ?? 21,
  });
  const masked = initial?.api_key_masked || "";
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);
  const set = (k, v) => setD({ ...d, [k]: v });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setTestRes(null);
    try {
      const payload = { base_url: d.base_url, mode: d.mode, default_tax_rate: parseFloat(d.default_tax_rate) };
      if ((d.api_key || "").trim()) payload.api_key = d.api_key.trim();
      await api.put("/settings/holded", payload);
      onSaved({ msg: { type: "ok", text: t("settings.holded.saved") } });
    } catch (ex) { onSaved({ msg: { type: "err", text: fmtErr(ex) } }); }
    finally { setSaving(false); }
  };

  const doTest = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/settings/holded/test");
      setTestRes(data);
    } catch (ex) { setTestRes({ ok: false, message: fmtErr(ex) }); }
    finally { setTesting(false); }
  };

  return (
    <form onSubmit={submit} className="sns-card p-5 space-y-4">
      <div>
        <h3 className="sns-title text-lg">{t("settings.holded.title")}</h3>
        <p className="text-xs text-slate-500 mt-1">{t("settings.holded.desc")}</p>
      </div>

      <details className="bg-slate-50 rounded-xl p-3 text-xs">
        <summary className="font-semibold cursor-pointer">{t("settings.holded.howto")}</summary>
        <p className="text-slate-600 mt-2">{t("settings.holded.howto_steps")}</p>
      </details>

      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("settings.holded.mode")}</label>
        <select value={d.mode} onChange={(e) => set("mode", e.target.value)} className="sns-input" data-testid="holded-mode-select">
          <option value="real">{t("settings.holded.mode.real")}</option>
          <option value="mock">{t("settings.holded.mode.mock")}</option>
        </select>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 mb-1 block">
          {t("settings.holded.key")} · <span className="text-slate-400 font-normal">{t("settings.holded.key.current")}: {masked || t("settings.holded.key.empty")}</span>
        </label>
        <input
          type="password"
          value={d.api_key}
          onChange={(e) => set("api_key", e.target.value)}
          placeholder={t("settings.holded.key.placeholder")}
          className="sns-input"
          autoComplete="off"
          data-testid="holded-api-key-input"
        />
        <p className="text-[11px] text-slate-400 mt-1">{t("settings.holded.key.hint")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("settings.holded.tax")}</label>
          <input type="number" step="1" min="0" max="100" value={d.default_tax_rate} onChange={(e) => set("default_tax_rate", e.target.value)} className="sns-input" data-testid="holded-tax-default" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">{t("settings.holded.base_url")}</label>
          <input value={d.base_url} onChange={(e) => set("base_url", e.target.value)} className="sns-input" data-testid="holded-base-url" />
        </div>
      </div>

      {testRes && (
        <div className={`text-sm rounded-xl p-3 ${testRes.ok ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="holded-test-result">
          {testRes.ok ? "✓ " : "✗ "}{testRes.message}
        </div>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={doTest} disabled={testing} className="sns-btn sns-btn-ghost bg-slate-100 flex-1" data-testid="btn-test-holded">
          {testing ? t("common.testing") : t("settings.holded.test")}
        </button>
        <button disabled={saving} className="sns-btn sns-btn-accent flex-1" data-testid="btn-save-holded">
          {saving ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </form>
  );
}

function IdiomaForm({ lang, setLang, t }) {
  return (
    <div className="sns-card p-5 space-y-4">
      <div>
        <h3 className="sns-title text-lg">{t("settings.lang.title")}</h3>
        <p className="text-xs text-slate-500 mt-1">{t("settings.lang.desc")}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setLang("es")}
          className={`p-4 rounded-2xl border-2 transition-all ${lang === "es" ? "border-[var(--coral)] bg-orange-50" : "border-[var(--border)] hover:border-slate-300"}`}
          data-testid="btn-lang-es"
        >
          <div className="text-3xl mb-2">🇪🇸</div>
          <div className="font-bold">{t("settings.lang.es")}</div>
          {lang === "es" && <div className="text-xs text-[var(--coral)] font-semibold mt-1">✓ Activo</div>}
        </button>
        <button
          type="button"
          onClick={() => setLang("en")}
          className={`p-4 rounded-2xl border-2 transition-all ${lang === "en" ? "border-[var(--coral)] bg-orange-50" : "border-[var(--border)] hover:border-slate-300"}`}
          data-testid="btn-lang-en"
        >
          <div className="text-3xl mb-2">🇬🇧</div>
          <div className="font-bold">{t("settings.lang.en")}</div>
          {lang === "en" && <div className="text-xs text-[var(--coral)] font-semibold mt-1">✓ Active</div>}
        </button>
      </div>
    </div>
  );
}

